import { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, List, ListItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CollapsibleText from './collapsible-text';

interface DataViewerProps {
  data: Record<string, unknown>;
  level?: number;
}

const formatKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const renderValue = (value: unknown, level: number): React.ReactNode => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" sx={{ color: '#6C6D76', fontSize: '0.75rem' }}>
        —
      </Typography>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <Typography variant="body2" sx={{ color: value ? 'success.main' : 'error.main', fontSize: '0.75rem' }}>
        {value ? 'Yes' : 'No'}
      </Typography>
    );
  }

  if (typeof value === 'number') {
    return (
      <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.75rem' }}>
        {value}
      </Typography>
    );
  }

  if (typeof value === 'string') {
    if (value.length === 0) {
      return (
        <Typography variant="body2" sx={{ color: '#6C6D76', fontSize: '0.75rem' }}>
          (empty)
        </Typography>
      );
    }
    return <CollapsibleText text={value} maxLength={80} />;
  }

  if (Array.isArray(value)) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return <ArrayViewer items={value} level={level} />;
  }

  if (typeof value === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return <ReviewsViewer data={value as Record<string, unknown>} level={level + 1} />;
  }

  return (
    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
      {String(value)}
    </Typography>
  );
};

interface ArrayViewerProps {
  items: unknown[];
  level: number;
}

function ArrayViewer({ items, level }: ArrayViewerProps) {
  const [expanded, setExpanded] = useState(items.length <= 3);

  if (items.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.75rem' }}>
        (empty list)
      </Typography>
    );
  }

  const displayItems = expanded ? items : items.slice(0, 2);

  return (
    <Box>
      <List dense disablePadding sx={{ pl: 1 }}>
        {displayItems.map((item, index) => {
          const isObject = typeof item === 'object' && item !== null;

          return (
            <ListItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              disablePadding
              sx={{
                display: 'block',
                py: 0.25,
                borderLeft: isObject ? '3px solid' : '1px solid',
                borderColor: isObject ? 'primary.main' : '#0000001f',
                pl: 1,
                mb: isObject ? 3 : 0.5,
                backgroundColor: isObject ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                borderRadius: isObject ? 1 : 0,
                pb: isObject ? 2 : 0.25,
                pt: isObject ? 1 : 0.25,
              }}
            >
              {isObject ? (
                <Box>{renderValue(item, level)}</Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      minWidth: 16,
                    }}
                  >
                    {index + 1}.
                  </Typography>
                  {renderValue(item, level)}
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>
      {items.length > 2 && (
        <Typography
          variant="body2"
          onClick={() => setExpanded(!expanded)}
          sx={{
            fontSize: '0.7rem',
            color: 'primary.main',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            p: 0,
            ml: 1,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {expanded ? 'Show less' : `${items.length - 2} more...`}
        </Typography>
      )}
    </Box>
  );
}

function ReviewsViewer({ data, level = 0 }: DataViewerProps) {
  const entries = Object.entries(data);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.75rem' }}>
        (no data)
      </Typography>
    );
  }

  return (
    <Box>
      {entries.map(([key, value]) => {
        const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
        const isArray = Array.isArray(value);
        const needsAccordion = isObject || (isArray && value.length > 0);

        if (needsAccordion) {
          return (
            <Accordion
              key={key}
              defaultExpanded={false}
              sx={{
                borderBottom: '1px solid',
                borderColor: '#0000001f',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                  px: 1,
                  '& .MuiAccordionSummary-content': {
                    overflow: 'hidden',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatKey(key)}
                  {isArray && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        ml: 0.5,
                      }}
                    >
                      ({(value as unknown[]).length})
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>{renderValue(value, level)}</AccordionDetails>
            </Accordion>
          );
        }

        return (
          <Box
            key={key}
            sx={{
              py: 0.5,
              px: 1,
              borderBottom: '1px solid',
              borderColor: '#0000001f',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                mb: 0.25,
                color: '#000000',
              }}
            >
              {formatKey(key)}
            </Typography>
            {renderValue(value, level)}
          </Box>
        );
      })}
    </Box>
  );
}

export default ReviewsViewer;
